import { cn } from "@design-system/utils";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { Button } from "../button";
import { Input } from "../input";

interface InputGroupControlId {
  controlId: string | undefined;
  setControlId: (controlId: string) => void;
}

const InputGroupControlIdContext = React.createContext<InputGroupControlId>({
  controlId: undefined,
  setControlId: () => undefined,
});

function InputGroup({ className, ...props }: React.ComponentProps<"div">) {
  const [controlId, setControlId] = React.useState(React.useId());
  const controlIdContext = React.useMemo(
    () => ({ controlId, setControlId }),
    [controlId]
  );

  return (
    <InputGroupControlIdContext.Provider value={controlIdContext}>
      <div
        data-slot="input-group"
        role="group"
        className={cn(
          "group/input-group bg-shape-neutral-outlined-background border-shape-neutral-outlined-border-emphasised relative flex h-9 w-full min-w-0 items-center rounded-md border shadow-xs transition-[color,box-shadow] outline-none has-[[data-slot=input-group-control]:disabled]:opacity-50",

          "has-[>[data-align=inline-start]]:[&>input]:pl-2",
          "has-[>[data-align=inline-end]]:[&>input]:pr-2",
          "has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-start]]:[&>input]:pb-3",
          "has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-end]]:[&>input]:pt-3",

          "has-[[data-slot=input-group-control]:focus-visible]:ring-ring-default has-[[data-slot=input-group-control]:focus-visible]:ring-[3px]",

          "has-[[data-slot][aria-invalid=true]]:border-shape-critical-outlined-border-emphasised",

          className
        )}
        {...props}
      />
    </InputGroupControlIdContext.Provider>
  );
}

const inputGroupAddonVariants = cva(
  "text-shape-neutral-outlined-foreground-muted flex h-auto items-center justify-center gap-2 py-1.5 text-sm font-medium select-none [&>svg:not([class*='size-'])]:size-4",
  {
    variants: {
      align: {
        "inline-start": "order-first pl-3 has-[>button]:ml-[-0.45rem]",
        "inline-end": "order-last pr-3 has-[>button]:mr-[-0.375rem]",
        "block-start":
          "order-first w-full justify-start px-3 pt-3 group-has-[>input]/input-group:pt-2.5 [.border-b]:pb-3",
        "block-end":
          "order-last w-full justify-start px-3 pb-3 group-has-[>input]/input-group:pb-2.5 [.border-t]:pt-3",
      },
    },
    defaultVariants: {
      align: "inline-start",
    },
  }
);

function InputGroupAddon({
  className,
  align = "inline-start",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof inputGroupAddonVariants>) {
  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      {...props}
    />
  );
}

function InputGroupLabelAddon({
  className,
  align = "inline-start",
  ...props
}: React.ComponentProps<"label"> &
  VariantProps<typeof inputGroupAddonVariants>) {
  const { controlId } = React.useContext(InputGroupControlIdContext);

  return (
    <label
      htmlFor={controlId}
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      {...props}
    />
  );
}

const inputGroupButtonVariants = cva(
  "flex items-center gap-2 text-sm shadow-none",
  {
    variants: {
      size: {
        xs: "h-6 gap-1 rounded-md px-2 has-[>svg]:px-2 [&>svg:not([class*='size-'])]:size-3.5",
        sm: "h-8 gap-1.5 rounded-md px-2.5 has-[>svg]:px-2.5",
        "icon-xs": "size-6 rounded-md p-0 has-[>svg]:p-0",
        "icon-sm": "size-8 p-0 has-[>svg]:p-0",
      },
    },
    defaultVariants: {
      size: "xs",
    },
  }
);

function InputGroupButton({
  className,
  type = "button",
  variant = "ghost",
  size = "xs",
  ...props
}: Omit<React.ComponentProps<typeof Button>, "size"> &
  VariantProps<typeof inputGroupButtonVariants>) {
  return (
    <Button
      type={type}
      data-size={size}
      variant={variant}
      className={cn(inputGroupButtonVariants({ size }), className)}
      {...props}
    />
  );
}

function InputGroupText({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      className={cn(
        "text-shape-neutral-outlined-foreground-muted flex items-center gap-2 text-sm [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    />
  );
}

function InputGroupInput({
  className,
  id,
  ...props
}: React.ComponentProps<typeof Input>) {
  const { controlId, setControlId } = React.useContext(
    InputGroupControlIdContext
  );

  React.useEffect(() => {
    if (id) {
      setControlId(id);
    }
  }, [id, setControlId]);

  return (
    <Input
      id={id ?? controlId}
      data-slot="input-group-control"
      className={cn(
        "flex-1 rounded-none border-0 bg-transparent shadow-none focus-visible:ring-0 disabled:opacity-100",
        className
      )}
      {...props}
    />
  );
}

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupLabelAddon,
  InputGroupText,
};
